import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  blankRow,
  sortByCode,
  sortByPhotographerCode,
  spreadShuffle,
} from "@/lib/liveentry-helpers";

// Owns the row grid state + every mutation that touches it. Lives outside
// LiveEntry.jsx so the page component is just layout + composition.
//
// Contract:
//   comp     — { id, … } | null while loading
//   canEdit  — bool, gates persistence (read-only viewers still see rows)
//   photogs  — roster from /photographers, used by add-all + reorder logic
//
// Returned mutators are intentionally not memoised individually because the
// page passes them straight into a child for the lifetime of the page; the
// few that DO want stable refs (saveRow, persistOrder) use useCallback.
export function useLiveEntryRows(comp, canEdit, photogs) {
  const [rows, setRows] = useState([blankRow()]);
  // Latest-rows mirror. saveRow needs to read the freshest row (esp. its
  // id after the first POST completes) to avoid closure staleness that
  // caused duplicate POSTs and lost placement selections on quick
  // successive edits.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  const updateRow = (key, patch) =>
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  const removeRow = async (row) => {
    if (row.id) {
      if (!window.confirm("Delete this entry?")) return;
      try {
        await api.delete(`/entries/${row.id}`);
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Unknown error";
        console.error("Failed to delete entry:", detail);
        window.alert(`Could not delete this entry: ${detail}`);
        return;
      }
    }
    setRows((rs) => {
      const filtered = rs.filter((r) => r._key !== row._key);
      return filtered.length ? filtered : [blankRow()];
    });
  };

  const addRow = () => setRows((rs) => [...rs, blankRow()]);

  // POST the current row order to /api/entries/reorder so server-side
  // sort_order matches what's on screen. The setRows-as-getter trick keeps
  // this stable across closures without ref churn.
  const persistOrder = useCallback(async () => {
    if (!comp) return;
    setRows((current) => {
      const ids = current.map((r) => r.id).filter(Boolean);
      if (ids.length) {
        api.post("/entries/reorder", {
          competition_id: comp.id,
          entry_ids: ids,
        }).catch((ex) => {
          console.warn("Reorder failed:", ex?.message || ex);
        });
      }
      return current;
    });
  }, [comp]);

  const addAllPhotographers = async () => {
    if (!comp || !canEdit) return;
    // Sort the roster by I.D. (photographer_code) numeric-aware, matching the
    // default order on the Photographers page. The judging order will still
    // be randomised on competition night via the Randomise button — this is
    // just the deterministic insertion order so clubs can scan/verify quickly.
    const sorted = [...photogs]
      .filter((p) => p.active_status !== false)
      .filter((p) => p.enters_competitions !== false)
      .sort((a, b) => sortByCode(a, b));

    const placeholders = sorted.map((p) => ({
      ...blankRow(),
      photographer_id: p.id,
      photographer_code: p.photographer_code,
      photographer_name: p.display_name,
      image_title: "",
      score: "",
      placement: "NONE",
      status: "saving",
    }));
    setRows((rs) => {
      const meaningful = rs.filter(
        (r) => r.photographer_id || r.image_title || r.score !== ""
      );
      const combined = [...meaningful, ...placeholders].sort((a, b) =>
        sortByPhotographerCode(a, b, photogs)
      );
      return [...combined, blankRow()];
    });

    const results = await Promise.all(
      placeholders.map(async (row) => {
        try {
          const { data } = await api.post("/entries", {
            competition_id: comp.id,
            photographer_id: row.photographer_id,
            image_title: "",
            score: null,
            placement: "NONE",
          });
          return { key: row._key, ok: true, data };
        } catch (ex) {
          return {
            key: row._key,
            ok: false,
            error: formatApiError(ex.response?.data?.detail) || ex.message,
          };
        }
      })
    );

    setRows((rs) =>
      rs.map((r) => {
        const res = results.find((x) => x.key === r._key);
        if (!res) return r;
        if (res.ok) {
          return {
            ...r,
            id: res.data.id,
            status: "saved",
            error: "",
            photographer_code: res.data.photographer_code || r.photographer_code,
            photographer_name: res.data.photographer_name || r.photographer_name,
          };
        }
        return { ...r, status: "error", error: res.error };
      })
    );

    await persistOrder();
  };

  const randomiseRows = () => {
    setRows((rs) => {
      const meaningful = rs.filter(
        (r) => r.photographer_id || r.image_title || r.score !== ""
      );
      if (meaningful.length < 2) return rs;
      const shuffled = spreadShuffle(meaningful);
      return [...shuffled, blankRow()];
    });
    setTimeout(() => { persistOrder(); }, 50);
  };

  const sortRowsAlpha = () => {
    setRows((rs) => {
      const meaningful = rs.filter(
        (r) => r.photographer_id || r.image_title || r.score !== ""
      );
      if (meaningful.length < 2) return rs;
      const sorted = [...meaningful].sort((a, b) => {
        const an = (a.photographer_name || "").toLowerCase();
        const bn = (b.photographer_name || "").toLowerCase();
        if (an !== bn) return an.localeCompare(bn);
        return (a.image_title || "").toLowerCase().localeCompare((b.image_title || "").toLowerCase());
      });
      return [...sorted, blankRow()];
    });
    setTimeout(() => { persistOrder(); }, 50);
  };

  // Sort rows by I.D. (photographer_code) using numeric-aware comparison
  // — matches the default order on the Photographers page (2, 3, 10, A1).
  const sortRowsByCode = () => {
    setRows((rs) => {
      const meaningful = rs.filter(
        (r) => r.photographer_id || r.image_title || r.score !== ""
      );
      if (meaningful.length < 2) return rs;
      const sorted = [...meaningful].sort((a, b) =>
        sortByPhotographerCode(a, b, photogs)
      );
      return [...sorted, blankRow()];
    });
    setTimeout(() => { persistOrder(); }, 50);
  };

  // Save logic — persists a row whenever it has a photographer assigned.
  // Title and score are optional (draft entries allowed for roster pre-loading).
  const saveRow = useCallback(async (rowOrOverrides) => {
    if (!comp || !canEdit) return;
    // Look up the latest row from our ref so we always see the fresh
    // server-assigned `id` after a prior save. Callers pass either the
    // whole row (legacy) or a partial with _key + overrides — either
    // way we resolve against rowsRef so closures can't stale us out.
    const latest = rowsRef.current.find((r) => r._key === rowOrOverrides._key);
    if (!latest) return;
    const row = { ...latest, ...rowOrOverrides };
    if (!row.photographer_id) return;

    let scoreNum = null;
    if (row.score !== "" && row.score !== null && row.score !== undefined) {
      const n = Number(row.score);
      if (Number.isNaN(n) || n < 0) {
        setRows((rs) => rs.map((r) => (r._key === row._key
          ? { ...r, status: "error", error: "Score must be a positive number" }
          : r)));
        return;
      }
      scoreNum = n;
    }

    setRows((rs) => rs.map((r) => (r._key === row._key ? { ...r, status: "saving", error: "" } : r)));
    try {
      const payload = {
        competition_id: comp.id,
        photographer_id: row.photographer_id,
        image_title: (row.image_title || "").trim(),
        score: scoreNum,
        placement: row.placement || "NONE",
      };
      const { data } = row.id
        ? await api.patch(`/entries/${row.id}`, payload)
        : await api.post(`/entries`, payload);
      setRows((rs) => rs.map((r) => (r._key === row._key ? {
        ...r,
        id: data.id,
        status: "saved",
        error: "",
        photographer_code: data.photographer_code,
        photographer_name: data.photographer_name,
      } : r)));
      return data;
    } catch (ex) {
      setRows((rs) => rs.map((r) => (r._key === row._key ? {
        ...r,
        status: "error",
        error: formatApiError(ex.response?.data?.detail) || ex.message,
      } : r)));
    }
  }, [comp, canEdit]);

  // Duplicate title detection (warning only).
  useEffect(() => {
    const titles = {};
    rows.forEach((r) => {
      const t = (r.image_title || "").trim().toLowerCase();
      if (!t) return;
      titles[t] = (titles[t] || 0) + 1;
    });
    setRows((rs) =>
      rs.map((r) => {
        const t = (r.image_title || "").trim().toLowerCase();
        const dup = t && titles[t] > 1;
        return r.duplicateWarn === dup ? r : { ...r, duplicateWarn: !!dup };
      })
    );
  }, [rows.map((r) => r.image_title).join("|")]);

  return {
    rows,
    setRows,
    updateRow,
    removeRow,
    addRow,
    persistOrder,
    addAllPhotographers,
    randomiseRows,
    sortRowsAlpha,
    sortRowsByCode,
    saveRow,
  };
}
